import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, defer, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private authService: AuthService | null = null;
  private messageService: MessageService | null = null;
  private translateService: TranslateService | null = null;

  // Prevents duplicate "session expired" toasts when multiple concurrent requests fail refresh
  private static sessionExpiredNotified = false;

  // Same guard for the "logged in elsewhere" toast. Kept separate from
  // sessionExpiredNotified: the two cases have different texts and one must
  // not suppress the other.
  private static sessionRevokedNotified = false;

  constructor(
    private injector: Injector,
    private router: Router
  ) {}

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  private getMessageService(): MessageService {
    if (!this.messageService) {
      this.messageService = this.injector.get(MessageService);
    }
    return this.messageService;
  }

  private getTranslateService(): TranslateService {
    if (!this.translateService) {
      this.translateService = this.injector.get(TranslateService);
    }
    return this.translateService;
  }

  private notifySessionExpiredOnce(): void {
    if (AuthInterceptor.sessionExpiredNotified) return;
    AuthInterceptor.sessionExpiredNotified = true;
    const t = this.getTranslateService();
    this.getMessageService().add({
      severity: 'warn',
      summary: t.instant('auth.session_expired_summary'),
      detail: t.instant('auth.session_expired_detail'),
      life: 5000,
    });
    this.router.navigate(['/auth/login']).then(() => {
      setTimeout(() => { AuthInterceptor.sessionExpiredNotified = false; }, 5000);
    });
  }

  /**
   * Session was revoked because the account signed in somewhere else.
   * Deliberately NOT notifySessionExpiredOnce: refreshing would be pointless
   * here, and the user needs to know why they were kicked out.
   */
  private notifySessionRevokedOnce(message?: string): void {
    if (AuthInterceptor.sessionRevokedNotified) return;
    AuthInterceptor.sessionRevokedNotified = true;
    const t = this.getTranslateService();
    this.getMessageService().add({
      severity: 'warn',
      summary: t.instant('auth.session_revoked_summary'),
      detail: message || t.instant('auth.session_revoked_detail'),
      life: 7000,
    });
    this.router.navigate(['/auth/login']).then(() => {
      setTimeout(() => { AuthInterceptor.sessionRevokedNotified = false; }, 7000);
    });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip interceptor for auth endpoints to prevent refresh recursion.
    // Logout must still go through to attach Authorization header.
    if (this.isAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    // Proactively refresh a token that is about to expire, so normal requests
    // do not have to fail with 401 first. Auth endpoints are already excluded
    // above, which is what keeps this from recursing into the refresh call.
    return defer(() => this.getAuthService().ensureTokenValid()).pipe(
      switchMap(() => this.handleWithToken(request, next)),
    );
  }

  private handleWithToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Add auth token to request
    const token = this.getAuthService().getAccessToken();
    if (token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Don't redirect to login for non-critical endpoints (language change, etc.)
          if (this.isNonCriticalEndpoint(request.url)) {
            return throwError(() => error);
          }
          return this.handle401Error(request, next, error.error);
        }
        if (error instanceof HttpErrorResponse && error.status === 403) {
          this.router.navigate(['/access-denied']);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    errorBody?: unknown,
  ): Observable<HttpEvent<unknown>> {
    const code = (errorBody as { code?: string } | undefined)?.code;

    // SESSION_REVOKED means a newer login took over this account - refreshing
    // would just fail, so go straight to the login screen with an explanation.
    if (code === 'SESSION_REVOKED') {
      this.getAuthService().clearTokens();
      this.notifySessionRevokedOnce((errorBody as { message?: string } | undefined)?.message);
      return throwError(() => new Error('Session revoked'));
    }

    // TOKEN_EXPIRED (or no code at all) keeps the existing refresh flow.
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.getAuthService().getRefreshToken();
      if (!refreshToken && this.getAuthService().isImpersonating()) {
        // During impersonation there is no refresh token — stop impersonation and restore original session
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        this.getAuthService().stopImpersonation();
        return throwError(() => new Error('Impersonation token expired'));
      }
      if (refreshToken) {
        // Use defer() so any synchronous throw inside refreshToken() becomes an Observable error
        return defer(() => this.getAuthService().refreshToken()).pipe(
          switchMap((tokens) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(tokens.access_token);
            return next.handle(this.addToken(request, tokens.access_token));
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(null);
            this.getAuthService().clearTokens();
            this.notifySessionExpiredOnce();
            return throwError(() => error);
          })
        );
      } else {
        this.isRefreshing = false;
        this.getAuthService().clearTokens();
        this.notifySessionExpiredOnce();
        return throwError(() => new Error('No refresh token'));
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token as string)))
      );
    }
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('/auth/login') ||
           url.includes('/auth/register') ||
           url.includes('/auth/token/refresh') ||
           url.includes('/auth/forgot-password') ||
           url.includes('/auth/reset-password') ||
           url.includes('/company/auth/login') ||
           url.includes('/company/auth/register') ||
           url.includes('/company/auth/token/refresh') ||
           url.includes('/company/auth/forgot-password') ||
           url.includes('/company/auth/reset-password');
  }

  /**
   * Non-critical endpoints: 401 should NOT trigger redirect to login.
   * Error is passed through to the caller for graceful handling.
   */
  private isNonCriticalEndpoint(url: string): boolean {
    return url.includes('/auth/language') ||
           url.includes('/company/auth/language') ||
           url.includes('/auth/logout') ||
           url.includes('/company/auth/logout');
  }
}
